/**
 * @element qds-main-toolbar
 * 
 * A responsive main toolbar component that organizes navigation and user controls.
 * Provides three slots for flexible content arrangement:
 * - left: typically contains navigation elements like menu, logo, and app name
 * - center: typically contains main content or can be left empty
 * - right: typically contains user controls like notifications, settings, avatar
 * 
 * On mobile screens, the component automatically hides the logo and 
 * converts the right section content into a dropdown menu with a hamburger icon.
 * 
 * Features a gradient line at the bottom of the toolbar.
 * 
 * @attr {boolean} compact - When true, displays a compact toolbar (52px height); when false or not set, displays standard size (96px height)
 * @attr {boolean} dark - When true, displays the dark theme version of the toolbar
 * @attr {string} site-name - The name of the site to display next to the logo
 * @attr {string} site-name-long - Full site name shown as a tooltip when the default logo or site name is hovered
 * @attr {string} app-version - The version of the application to display as a subscript next to the site name
 * @attr {string} auth-client-id - Azure AD application client ID for authentication (required for auth)
 * @attr {string} auth-authority - Azure AD authority URL (optional, defaults to Quantum tenant)
 * @attr {string} auth-scopes - Comma-separated list of OAuth scopes (defaults to User.Read)
 * @attr {string} auth-redirect-uri - OAuth redirect URI (defaults to window.location.origin)
 * @attr {string} auth-cache-location - Token cache location: localStorage or sessionStorage (defaults to localStorage)
 * @attr {string} auth-mode - `qds` (default) initializes QDS auth; `external` leaves authentication to the host application
 * @attr {string} main-menu-data - JSON string containing menu items for the horizontal menu in the center slot
 * @attr {boolean} show-help-button - When true, displays the help button in the right slot; when false, hides it
 * @attr {boolean} show-settings-button - When true, displays the settings button in the right slot; when false or not set, hides it
 * @attr {boolean} show-theme-switch - When true, displays the theme switch in the right slot; when false or not set, hides it
 * @attr {boolean} show-notification-tray - When true, displays the notification tray in the right slot; when false or not set, hides it
 * @attr {boolean} show-github-button - When true, displays a GitHub shortcut button in the right slot; when false or not set, hides it
 * @attr {string} github-url - The URL opened when the built-in GitHub button is activated
 * @attr {boolean} show-mobile-gradient-line - Controls mobile gradient line visibility. Use "true" (default) or "false"
 * @attr {number} mobile-breakpoint - Max viewport width in pixels where the toolbar switches to mobile mode (defaults to 480)
 * 
 * @slot left - Content for the left side of the toolbar
 * @slot center - Content for the center of the toolbar
 * @slot right - Content for the right side of the toolbar
 * 
 * @csspart toolbar - The main toolbar container
 * @csspart left-section - The left section of the toolbar
 * @csspart center-section - The center section of the toolbar
 * @csspart right-section - The right section of the toolbar
 * @csspart github-button - The built-in GitHub action button
 * @csspart mobile-menu - The mobile dropdown menu container
 * @csspart hamburger-button - The hamburger menu button for mobile view
 * @csspart site-name - The site name text element
 * @csspart app-version - The app version text element
 * 
 * @fires versionClicked - Fired when the app version is clicked. The event detail contains the version string.
 * 
 * @method getHelpButton - Returns the help button component in the right slot
 * @method getNotificationTray - Returns the notification tray component in the right slot
 * @method getUserAvatar - Returns the user avatar component in the right slot
 * @method getAuthAvatar - Returns the auth avatar component in the right slot (if auth is configured)
 * @method getSettingsButton - Returns the settings button component in the right slot
 * @method getGithubButton - Returns the built-in GitHub button in the right slot
 * @method getMainMenu - Returns the horizontal menu component in the center slot
 */
import '../gradient-line/qds-gradient-line.js';
import '../company-logo/qds-company-logo.js';
import '../user-avatar/qds-user-avatar.js';
import '../user-avatar/qds-auth-avatar.js';
import '../settings-button/qds-settings-button.js';
import '../notification-tray/qds-notification-tray.js';
import '../help-button/qds-help-button.js';
import '../horizontal-menu/qds-horizontal-menu.js';
import '../theme-switch/qds-theme-switch.js';
import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

const MOBILE_AVATAR_MENU_IDS = {
  help: 'qds-toolbar-mobile-help',
  github: 'qds-toolbar-mobile-github',
  settings: 'qds-toolbar-mobile-settings',
  theme: 'qds-toolbar-mobile-theme'
} as const;

const MOBILE_AVATAR_MENU_ICONS = {
  help: '<i class="pi pi-question-circle" aria-hidden="true"></i>',
  github: '<i class="pi pi-github" aria-hidden="true"></i>',
  settings: '<i class="pi pi-cog" aria-hidden="true"></i>'
} as const;

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

class QdsMainToolbar extends HTMLElement {
  // Private properties
  private _mobileMenuOpen: boolean;
  private _compact: boolean;
  private _siteName: string;
  private _siteNameLong: string;
  private _appVersion: string;
  private _authClientId: string;
  private _authAuthority: string;
  private _authScopes: string;
  private _authRedirectUri: string;
  private _authCacheLocation: string;
  private _authMode: 'qds' | 'external';
  private _mainMenuData: string | null;
  private _showHelpButton: boolean;
  private _showSettingsButton: boolean;
  private _showThemeSwitch: boolean;
  private _showNotificationTray: boolean;
  private _showGithubButton: boolean;
  private _githubUrl: string;
  private _showMobileGradientLine: boolean;
  private _mobileBreakpoint: number;
  private _documentClickHandler: ((event: MouseEvent) => void) | null;
  private _resizeHandler: (() => void) | null;
  private _slotObserver: MutationObserver | null;
  private _boundMobileMenuToggle: (event: MouseEvent) => void;
  private _boundMobileToolbarMenuToggle: EventListener;
  private _boundVersionClick: (event: MouseEvent) => void;
  private _boundGithubButtonClick: (event: MouseEvent) => void;
  private _boundMobileMainMenuSelect: EventListener;
  private _boundThemeChangeHandler: EventListener;

  static get observedAttributes(): string[] {
    return [
      'compact',
      'dark',
      'site-name',
      'site-name-long',
      'app-version',
      'auth-client-id',
      'auth-authority',
      'auth-scopes',
      'auth-redirect-uri',
      'auth-cache-location',
      'auth-mode',
      'main-menu-data',
      'show-help-button',
      'show-settings-button',
      'show-theme-switch',
      'show-notification-tray',
      'show-github-button',
      'github-url',
      'show-mobile-gradient-line',
      'mobile-breakpoint'
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Initialize default property values
    this._mobileMenuOpen = false;
    this._compact = false; // Default to standard size
    this._siteName = ''; // Default empty site name
    this._siteNameLong = ''; // Default empty site name tooltip
    this._appVersion = ''; // Default empty app version
    this._authClientId = '';
    this._authAuthority = '';
    this._authScopes = 'User.Read';
    this._authRedirectUri = '';
    this._authCacheLocation = 'localStorage';
    this._authMode = 'qds';
    this._mainMenuData = null; // Default to no menu data
    this._showHelpButton = true; // Default to show help button
    this._showSettingsButton = false; // Default to hide settings button
    this._showThemeSwitch = false; // Default to hide theme switch
    this._showNotificationTray = true; // Default to show notification tray
    this._showGithubButton = false; // Default to hide GitHub button
    this._githubUrl = ''; // Default to no GitHub URL
    this._showMobileGradientLine = true; // Default to show gradient line in mobile
    this._mobileBreakpoint = 480;
    this._documentClickHandler = null;
    this._resizeHandler = null;
    this._slotObserver = null;
    this._boundMobileMenuToggle = this._handleMobileMenuToggle.bind(this);
    this._boundMobileToolbarMenuToggle = this._handleMobileToolbarMenuToggle.bind(this) as EventListener;
    this._boundVersionClick = this._handleVersionClick.bind(this);
    this._boundGithubButtonClick = this._handleGithubButtonClick.bind(this);
    this._boundMobileMainMenuSelect = () => {
      this._toggleMobileMenu(false);
    };
    this._boundThemeChangeHandler = this._handleThemeStateChange.bind(this) as EventListener;

    this.render();
  }

  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;

    if (this._showNotificationTray) {
      this.setAttribute('show-notification-tray', '');
    }

    if (this._showHelpButton) {
      this.setAttribute('show-help-button', '');
    }

    if (this._showSettingsButton) {
      this.setAttribute('show-settings-button', '');
    }

    if (this._showThemeSwitch) {
      this.setAttribute('show-theme-switch', '');
    }

    if (this._showGithubButton) {
      this.setAttribute('show-github-button', '');
    }

    // Clone right slot content to mobile-right slot
    this._syncMobileContent();

    // Move right-side actions into the mobile menu when in mobile view
    this._positionMobileActions();
    this._syncMobileAvatarMenu();

    // Close mobile menu when clicking outside
    this._documentClickHandler = (event: MouseEvent) => {
      if (this._mobileMenuOpen) {
        const mobileMenu = this.shadowRoot?.querySelector('.mobile-menu');
        const hamburgerButton = this.shadowRoot?.querySelector('.hamburger-button');
        const mobileToolbarMenu = this.shadowRoot?.querySelector('.mobile-toolbar-menu');
        if (mobileMenu && hamburgerButton) {
          const target = event.target as Node;
          // Check if click is outside the menu and hamburger button
          if (!mobileMenu.contains(target) &&
            !hamburgerButton.contains(target) &&
            !(mobileToolbarMenu && mobileToolbarMenu.contains(target)) &&
            !event.composedPath().includes(mobileMenu) &&
            !event.composedPath().includes(hamburgerButton) &&
            !(mobileToolbarMenu && event.composedPath().includes(mobileToolbarMenu))) {
            this._toggleMobileMenu(false);
          }
        }
      }
    };

    document.addEventListener('click', this._documentClickHandler);

    // Handle resize events to close mobile menu when switching to desktop
    this._resizeHandler = () => {
      const isMobile = window.matchMedia(`(max-width: ${this._mobileBreakpoint}px)`).matches;
      this._positionMobileActions();
      this._syncMobileAvatarMenu();
      if (!isMobile && this._mobileMenuOpen) {
        this._toggleMobileMenu(false);
      }
    };

    window.addEventListener('resize', this._resizeHandler);
    document.addEventListener('qds-theme-change', this._boundThemeChangeHandler);

    // Setup mutation observer to sync content when slots change
    this._setupSlotChangeListener();

    // Apply main menu data if available
    if (this._mainMenuData) {
      this._updateMainMenu();
    }

    // Initialize auth service if auth configuration is provided
    if (this._usesQdsAuthentication() && this._hasAuthConfig()) {
      this._initializeAuth();
    }
  }

  /**
   * Check if auth configuration is complete
   * @returns true if clientId is provided (authority will default to Quantum tenant)
   * @private
   */
  private _hasAuthConfig(): boolean {
    return !!this._authClientId;
  }

  /**
   * Angular hosts use external mode so MSAL, API tokens, and user state have
   * one owner: the Quangular runtime. QDS remains self-managed by default for
   * framework-free consumers.
   */
  private _usesQdsAuthentication(): boolean {
    return this._authMode === 'qds';
  }

  /**
   * Build auth config object from individual attributes
   * @returns Auth configuration object
   * @private
   */
  private _buildAuthConfig(): any {
    const config: any = {
      clientId: this._authClientId,
      defaultScopes: this._authScopes.split(',').map(s => s.trim()).filter(s => s)
    };

    // Only add optional fields if they're explicitly set
    if (this._authAuthority) {
      config.authority = this._authAuthority;
    }

    if (this._authRedirectUri) {
      config.redirectUri = this._authRedirectUri;
    }

    if (this._authCacheLocation) {
      config.cacheLocation = this._authCacheLocation;
    }

    return config;
  }

  /**
   * Initialize the Quantum Auth service with the provided configuration
   * @private
   */
  private async _initializeAuth(): Promise<void> {
    try {
      // Check if auth service is already initialized globally
      if ((window as any).quantumAuth) {
        console.log('[qds-main-toolbar] Auth service already initialized, using existing instance');
        return;
      }

      const config = this._buildAuthConfig();
      console.log('[qds-main-toolbar] Initializing auth with config:', config);

      // Dynamically load the auth service module
      if (typeof (window as any).initializeQuantumAuth !== 'function') {
        console.log('[qds-main-toolbar] Loading auth service module...');
        const authModulePath = '/dev/assets/services/auth/index.js';
        await import(authModulePath);

        // Wait a bit for the module to register itself
        await new Promise(resolve => setTimeout(resolve, 100));

        if (typeof (window as any).initializeQuantumAuth !== 'function') {
          throw new Error('Auth service module loaded but initializeQuantumAuth not available');
        }

        console.log('[qds-main-toolbar] Auth service module loaded successfully');
      }

      // Double-check that auth wasn't initialized by another component while we were loading
      if ((window as any).quantumAuth) {
        console.log('[qds-main-toolbar] Auth service was initialized by another component, using that instance');
        return;
      }

      // Initialize auth service with the provided config
      console.log('[qds-main-toolbar] Calling initializeQuantumAuth...');
      const authService = (window as any).initializeQuantumAuth(config);
      (window as any).quantumAuth = authService;

      // Initialize the auth service
      if (authService && typeof authService.initialize === 'function') {
        console.log('[qds-main-toolbar] Initializing auth service...');
        await authService.initialize();
        console.log('[qds-main-toolbar] Auth service initialized');
      }

      // Trigger an event to notify components
      document.dispatchEvent(new CustomEvent('quantum-auth-initialized'));
    } catch (error) {
      console.error('[qds-main-toolbar] Error initializing auth:', error);
    }
  }
  
  /**
   * Duplicates elements from right slot to mobile-right slot
   * This ensures mobile menu has the same content as desktop right slot
   */
  private _syncMobileContent(): void {
    // Handle initial assignment
    const rightSlot = this.querySelector('[slot="right"]') as HTMLElement | null;
    if (rightSlot) {
      // Clone the content
      const clone = rightSlot.cloneNode(true) as HTMLElement;
      clone.setAttribute('slot', 'mobile-right');
      clone.classList.add('mobile-slot');

      // Remove any existing mobile-right slotted elements
      const existingMobileContent = this.querySelector('[slot="mobile-right"]');
      if (existingMobileContent) {
        existingMobileContent.remove();
      }

      // Add the clone to the component
      this.appendChild(clone);
    }
  }

  /**
   * Sets up mutation observer to watch for changes in the right slot
   */
  private _setupSlotChangeListener(): void {
    // Watch for changes to child elements
    this._slotObserver = new MutationObserver((mutations) => {
      let needsSync = false;

      for (const mutation of mutations) {
        // Check if the mutation involves the right slot or its children
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const rightSlotChanged = Array.from(mutation.addedNodes).some(node =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).getAttribute && (node as Element).getAttribute('slot') === 'right'
          ) || Array.from(mutation.removedNodes).some(node =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).getAttribute && (node as Element).getAttribute('slot') === 'right'
          );

          const targetElement = mutation.target as Element;
          if (rightSlotChanged ||
            (targetElement.getAttribute && targetElement.getAttribute('slot') === 'right')) {
            needsSync = true;
            break;
          }
        }
      }

      if (needsSync) {
        this._syncMobileContent();
        this._positionMobileActions();
        this._syncMobileAvatarMenu();
      }
    });

    this._slotObserver.observe(this, {
      childList: true,
      attributes: true,
      subtree: true,
      attributeFilter: ['slot']
    });
  }

  disconnectedCallback(): void {
    // Remove event listeners
    const hamburgerButton = this.shadowRoot?.querySelector('.hamburger-button') as HTMLButtonElement | null;
    if (hamburgerButton) {
      hamburgerButton.removeEventListener('click', this._boundMobileMenuToggle);
    }

    // Remove app version click listener
    const appVersion = this.shadowRoot?.querySelector('#app-version') as HTMLElement | null;
    if (appVersion) {
      appVersion.removeEventListener('click', this._boundVersionClick);
    }

    const githubButton = this.getGithubButton();
    if (githubButton) {
      githubButton.removeEventListener('click', this._boundGithubButtonClick);
    }

    if (this._documentClickHandler) {
      document.removeEventListener('click', this._documentClickHandler);
      this._documentClickHandler = null;
    }

    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    document.removeEventListener('qds-theme-change', this._boundThemeChangeHandler);
    
    // Disconnect observer
    if (this._slotObserver) {
      this._slotObserver.disconnect();
      this._slotObserver = null;
    }

    // Remove any mobile slot content
    const mobileContent = this.querySelector('.mobile-slot');
    if (mobileContent) {
      mobileContent.remove();
    }
  }

  /**
   * Handler for mobile menu toggle click
   * @param event - Click event
   */
  private _handleMobileMenuToggle(event: MouseEvent): void {
    event.stopPropagation();
    // Keep mobile popups mutually exclusive.
    this._closeMobileToolbarMenu();
    this._toggleMobileMenu(!this._mobileMenuOpen);
  }

  /**
     * When hierarchical menu opens in mobile header, close hamburger popup.
     * @param event - Toggle event from qds-hierarchical-menu
     */
  private _handleMobileToolbarMenuToggle(event: CustomEvent): void {
    if (event?.detail?.isOpen && this._mobileMenuOpen) {
      this._toggleMobileMenu(false);
    }
  }

  /**
   * Closes the mobile header hierarchical menu if it is open.
   */
  private _closeMobileToolbarMenu() {
    const mobileToolbarMenu = this.shadowRoot?.querySelector('.mobile-toolbar-menu qds-hierarchical-menu') as any | null;
    if (mobileToolbarMenu && mobileToolbarMenu.isOpen && typeof mobileToolbarMenu.closeMenu === 'function') {
      mobileToolbarMenu.closeMenu();
    }
  }

  /**
   * Toggle mobile menu state
   * @param open - Whether to open or close the menu
   */
  private _toggleMobileMenu(open: boolean): void {
    this._mobileMenuOpen = open;

    const mobileMenu = this.shadowRoot?.querySelector('.mobile-menu') as HTMLElement | null;
    const hamburgerButton = this.shadowRoot?.querySelector('.hamburger-button') as HTMLButtonElement | null;

    if (mobileMenu && hamburgerButton) {
      if (open) {
        mobileMenu.classList.add('open');
        hamburgerButton.classList.add('active');
      } else {
        mobileMenu.classList.remove('open');
        hamburgerButton.classList.remove('active');
      }
    }
  }

  /**
     * Keeps the right section attached to the toolbar.
     * Mobile no longer reuses right-section controls in the popup menu.
     */
  private _positionMobileActions() {
    if (!this.shadowRoot)
      return;
    const toolbar = this.shadowRoot.querySelector('.toolbar');
    const rightSection = this.shadowRoot.querySelector('.right-section');
    const hamburgerButton = this.shadowRoot.querySelector('.hamburger-button');
    if (!toolbar || !rightSection || !hamburgerButton)
      return;
    if (rightSection.parentElement !== toolbar) {
      toolbar.insertBefore(rightSection, hamburgerButton);
    }
  }

  private _handleThemeStateChange(): void {
    this._syncMobileAvatarMenu();
  }

  private _isMobileView(): boolean {
    return window.matchMedia(`(max-width: ${this._mobileBreakpoint}px)`).matches;
  }

  private _getAvatarMenuTarget(): any | null {
    const authAvatar = this.getAuthAvatar() as any;
    if (authAvatar && typeof authAvatar.addMenuItem === 'function') {
      return authAvatar;
    }

    const userAvatar = this.getUserAvatar() as any;
    if (userAvatar && typeof userAvatar.addMenuItem === 'function') {
      return userAvatar;
    }

    return null;
  }

  private _removeInjectedAvatarMenuItems(): void {
    const avatar = this._getAvatarMenuTarget();
    if (!avatar || typeof avatar.removeMenuItem !== 'function') {
      return;
    }

    Object.values(MOBILE_AVATAR_MENU_IDS).forEach((id) => {
      avatar.removeMenuItem(id);
    });
  }

  private _invokeComponentButton(component: HTMLElement | null, selector: string = 'button'): void {
    const button = component?.shadowRoot?.querySelector(selector) as HTMLElement | null;
    if (button) {
      button.click();
      return;
    }

    component?.click();
  }

  private _getThemeSwitch(): HTMLElement | null {
    const slottedThemeSwitch = this._findAssignedComponent('right', 'qds-theme-switch');
    if (slottedThemeSwitch) {
      return slottedThemeSwitch;
    }

    return this.shadowRoot?.querySelector('.right-section qds-theme-switch') as HTMLElement | null;
  }

  private _getThemeMenuLabel(themeSwitch: any): string {
    const isDark = Boolean(themeSwitch?.dark ?? themeSwitch?.hasAttribute?.('dark'));
    return isDark ? 'Use Light Mode' : 'Use Dark Mode';
  }

  private _getThemeMenuIcon(themeSwitch: any): string {
    const isDark = Boolean(themeSwitch?.dark ?? themeSwitch?.hasAttribute?.('dark'));
    return `<i class="pi ${isDark ? 'pi-sun' : 'pi-moon'}" aria-hidden="true"></i>`;
  }

  private _syncMobileAvatarMenu(): void {
    this._removeInjectedAvatarMenuItems();

    if (!this._isMobileView()) {
      return;
    }

    const avatar = this._getAvatarMenuTarget();
    if (!avatar || typeof avatar.addMenuItem !== 'function') {
      return;
    }

    const helpButton = this.getHelpButton();
    const settingsButton = this.getSettingsButton();
    const themeSwitch = this._getThemeSwitch();

    if (helpButton) {
      avatar.addMenuItem(MOBILE_AVATAR_MENU_IDS.help, {
        label: 'Help',
        icon: MOBILE_AVATAR_MENU_ICONS.help,
        type: 'custom',
        action: () => helpButton.click()
      });
    }

    const githubUrl = this._getNormalizedGithubUrl();
    if (this._showGithubButton && githubUrl) {
      avatar.addMenuItem(MOBILE_AVATAR_MENU_IDS.github, {
        label: 'GitHub',
        icon: MOBILE_AVATAR_MENU_ICONS.github,
        type: 'custom',
        action: () => this._openGithubUrl(githubUrl)
      });
    }

    if (settingsButton) {
      avatar.addMenuItem(MOBILE_AVATAR_MENU_IDS.settings, {
        label: 'Settings',
        icon: MOBILE_AVATAR_MENU_ICONS.settings,
        type: 'custom',
        action: () => settingsButton.click()
      });
    }

    if (themeSwitch) {
      avatar.addMenuItem(MOBILE_AVATAR_MENU_IDS.theme, {
        label: this._getThemeMenuLabel(themeSwitch as any),
        icon: this._getThemeMenuIcon(themeSwitch as any),
        type: 'custom',
        action: () => {
          this._invokeComponentButton(themeSwitch, '#theme-button');
          setTimeout(() => this._syncMobileAvatarMenu(), 0);
        }
      });
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'compact':
        // Boolean attributes are present or not, but when they're present,
        // their value is an empty string or the attribute name
        this._compact = newValue !== null;
        this.render();
        break;
      case 'dark':
        this.render();
        break;
      case 'site-name':
        this._siteName = newValue || '';
        this.render();
        break;
      case 'site-name-long':
        this._siteNameLong = newValue || '';
        this.render();
        break;
      case 'app-version':
        this._appVersion = newValue || '';
        this.render();
        break;
      case 'auth-client-id':
        this._authClientId = newValue || '';
        // Re-initialize auth if config is now complete
        if (this.isConnected && this._usesQdsAuthentication() && this._hasAuthConfig()) {
          this._initializeAuth();
        }
        this.render();
        break;
      case 'auth-authority':
        this._authAuthority = newValue || '';
        this.render();
        break;
      case 'auth-scopes':
        this._authScopes = newValue || 'User.Read';
        this.render();
        break;
      case 'auth-redirect-uri':
        this._authRedirectUri = newValue || '';
        this.render();
        break;
      case 'auth-cache-location':
        this._authCacheLocation = newValue || 'localStorage';
        this.render();
        break;
      case 'auth-mode':
        this._authMode = newValue === 'external' ? 'external' : 'qds';
        this.render();
        break;
      case 'main-menu-data':
        this._mainMenuData = newValue;
        // Update the main menu if it exists
        this._updateMainMenu();
        this.render();
        break;
      case 'show-help-button':
        this._showHelpButton = newValue !== null;
        this.render();
        break;
      case 'show-settings-button':
        this._showSettingsButton = newValue !== null;
        this.render();
        break;
      case 'show-theme-switch':
        this._showThemeSwitch = newValue !== null;
        this.render();
        break;
      case 'show-notification-tray':
        this._showNotificationTray = newValue !== null;
        this.render();
        break;
      case 'show-github-button':
        this._showGithubButton = newValue !== null;
        this.render();
        break;
      case 'github-url':
        this._githubUrl = newValue || '';
        this.render();
        break;
      case 'show-mobile-gradient-line':
        this._showMobileGradientLine = newValue !== 'false';
        this.render();
        break;
      case 'mobile-breakpoint': {
        const parsed = Number.parseInt(newValue || '', 10);
        this._mobileBreakpoint = Number.isFinite(parsed) && parsed > 0 ? parsed : 480;
        this.render();
        break;
      }
    }
  }

  // Getters and setters for properties
  get compact(): boolean {
    return this._compact;
  }

  set compact(value: boolean) {
    if (value) {
      this.setAttribute('compact', '');
    } else {
      this.removeAttribute('compact');
    }
  }
  
  get dark(): boolean {
    return this.hasAttribute('dark');
  }
  
  set dark(value: boolean) {
    if (value) {
      this.setAttribute('dark', '');
    } else {
      this.removeAttribute('dark');
    }
  }
  
  get siteName(): string {
    return this._siteName;
  }
  
  set siteName(value: string) {
    this.setAttribute('site-name', value);
  }

  get siteNameLong(): string {
    return this._siteNameLong;
  }

  set siteNameLong(value: string) {
    this.setAttribute('site-name-long', value);
  }
  
  get appVersion(): string {
    return this._appVersion;
  }

  set appVersion(value: string) {
    this.setAttribute('app-version', value);
  }

  get authClientId(): string {
    return this._authClientId;
  }

  set authClientId(value: string) {
    this.setAttribute('auth-client-id', value);
  }

  get authAuthority(): string {
    return this._authAuthority;
  }

  set authAuthority(value: string) {
    this.setAttribute('auth-authority', value);
  }

  get authScopes(): string {
    return this._authScopes;
  }

  set authScopes(value: string) {
    this.setAttribute('auth-scopes', value);
  }

  get authRedirectUri(): string {
    return this._authRedirectUri;
  }

  set authRedirectUri(value: string) {
    this.setAttribute('auth-redirect-uri', value);
  }

  get authCacheLocation(): string {
    return this._authCacheLocation;
  }

  set authCacheLocation(value: string) {
    this.setAttribute('auth-cache-location', value);
  }

  get authMode(): 'qds' | 'external' {
    return this._authMode;
  }

  set authMode(value: 'qds' | 'external') {
    this.setAttribute('auth-mode', value);
  }

  get mainMenuData(): string | null {
    return this._mainMenuData;
  }
  
  set mainMenuData(value: string | null) {
    if (value) {
      this.setAttribute('main-menu-data', value);
    } else {
      this.removeAttribute('main-menu-data');
    }
  }

  get showHelpButton(): boolean {
    return this._showHelpButton;
  }

  set showHelpButton(value: boolean) {
    if (value) {
      this.setAttribute('show-help-button', '');
    } else {
      this.removeAttribute('show-help-button');
    }
  }

  get showSettingsButton(): boolean {
    return this._showSettingsButton;
  }

  set showSettingsButton(value: boolean) {
    if (value) {
      this.setAttribute('show-settings-button', '');
    } else {
      this.removeAttribute('show-settings-button');
    }
  }

  get showThemeSwitch(): boolean {
    return this._showThemeSwitch;
  }

  set showThemeSwitch(value: boolean) {
    if (value) {
      this.setAttribute('show-theme-switch', '');
    } else {
      this.removeAttribute('show-theme-switch');
    }
  }

  get showNotificationTray(): boolean {
    return this._showNotificationTray;
  }

  set showNotificationTray(value: boolean) {
    if (value) {
      this.setAttribute('show-notification-tray', '');
    } else {
      this.removeAttribute('show-notification-tray');
    }
  }

  get showGithubButton(): boolean {
    return this._showGithubButton;
  }

  set showGithubButton(value: boolean) {
    if (value) {
      this.setAttribute('show-github-button', '');
    } else {
      this.removeAttribute('show-github-button');
    }
  }

  get githubUrl(): string {
    return this._githubUrl;
  }

  set githubUrl(value: string) {
    if (value) {
      this.setAttribute('github-url', value);
    } else {
      this.removeAttribute('github-url');
    }
  }

  get showMobileGradientLine(): boolean {
    return this._showMobileGradientLine;
  }

  set showMobileGradientLine(value: boolean) {
    this.setAttribute('show-mobile-gradient-line', value ? 'true' : 'false');
  }

  private _findAssignedComponent(slotName: 'left' | 'center' | 'right', selector: string): HTMLElement | null {
    const slot = this.shadowRoot?.querySelector(`slot[name="${slotName}"]`) as HTMLSlotElement | null;
    if (!slot) {
      return null;
    }

    const assignedNodes = slot.assignedNodes();
    for (const node of assignedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      const element = node as Element;
      if (element.matches(selector)) {
        return element as HTMLElement;
      }

      const nestedMatch = element.querySelector(selector);
      if (nestedMatch) {
        return nestedMatch as HTMLElement;
      }
    }

    return null;
  }

  /**
   * Get the help button component in the right slot
   * @returns The help button or null if not present
   */
  getHelpButton(): HTMLElement | null {
    const slottedHelpButton = this._findAssignedComponent('right', 'qds-help-button');
    if (slottedHelpButton) {
      return slottedHelpButton;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.right-section qds-help-button') as HTMLElement | null;
  }

  /**
   * Get the notification tray component in the right slot
   * @returns The notification tray or null if not present
   */
  getNotificationTray(): HTMLElement | null {
    const slottedNotificationTray = this._findAssignedComponent('right', 'qds-notification-tray');
    if (slottedNotificationTray) {
      return slottedNotificationTray;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.right-section qds-notification-tray') as HTMLElement | null;
  }

  /**
   * Get the user avatar component in the right slot
   * @returns The user avatar or null if not present
   */
  getUserAvatar(): HTMLElement | null {
    const slottedUserAvatar = this._findAssignedComponent('right', 'qds-user-avatar');
    if (slottedUserAvatar) {
      return slottedUserAvatar;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.right-section qds-user-avatar') as HTMLElement | null;
  }

  /**
   * Get the auth avatar component in the right slot
   * @returns The auth avatar or null if not present
   */
  getAuthAvatar(): HTMLElement | null {
    const slottedAuthAvatar = this._findAssignedComponent('right', 'qds-auth-avatar');
    if (slottedAuthAvatar) {
      return slottedAuthAvatar;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.right-section qds-auth-avatar') as HTMLElement | null;
  }

  /**
   * Get the settings button component in the right slot
   * @returns The settings button or null if not present
   */
  getSettingsButton(): HTMLElement | null {
    const slottedSettingsButton = this._findAssignedComponent('right', 'qds-settings-button');
    if (slottedSettingsButton) {
      return slottedSettingsButton;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.right-section qds-settings-button') as HTMLElement | null;
  }

  /**
   * Get the built-in GitHub button in the right slot
   * @returns The GitHub button or null if not present
   */
  getGithubButton(): HTMLElement | null {
    const slottedGithubButton = this._findAssignedComponent('right', '[data-role="github-button"]');
    if (slottedGithubButton) {
      return slottedGithubButton;
    }

    return this.shadowRoot?.querySelector('.right-section [data-role="github-button"]') as HTMLElement | null;
  }

  /**
   * Get the horizontal menu component in the center slot
   * @returns The horizontal menu or null if not present
   */
  getMainMenu(): HTMLElement | null {
    const slottedMainMenu = this._findAssignedComponent('center', 'qds-horizontal-menu');
    if (slottedMainMenu) {
      return slottedMainMenu;
    }

    // If no custom one, return the default one
    return this.shadowRoot?.querySelector('.center-section qds-horizontal-menu') as HTMLElement | null;
  }

  /**
   * Get the mobile list menu component rendered inside the hamburger popup.
   * @returns The mobile horizontal menu or null if not found
   */
  getMobileMainMenu() {
    return this.shadowRoot?.querySelector('.mobile-main-menu qds-horizontal-menu');
  }
  
  /**
   * Updates the horizontal menu component with the main menu data
   * @private
   */
  private _updateMainMenu(): void {
    const menuData = this._mainMenuData || '[]';
    try {
      // Validate that the data is valid JSON
      const normalizedData = JSON.stringify(JSON.parse(menuData));
      // Set the data attribute on desktop and mobile menu views
      const mainMenu = this.getMainMenu();
      if (mainMenu) {
        mainMenu.setAttribute('data', normalizedData);
      }
      const mobileMainMenu = this.getMobileMainMenu();
      if (mobileMainMenu) {
        mobileMainMenu.setAttribute('data', normalizedData);
      }

    } catch (e) {
      console.error('Invalid main-menu-data JSON:', e);
    }
  }

  /**
   * Whether the configured main menu contains any items.
   * @private
   */
  private _hasMainMenuItems(): boolean {
    if (!this._mainMenuData) {
      return false;
    }

    try {
      const parsed = JSON.parse(this._mainMenuData);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch (_error) {
      return false;
    }
  }
  
  /**
   * Handles click on the app version text
   * @param event - The click event
   * @private
   */
  private _handleVersionClick(event: MouseEvent): void {
    event.stopPropagation();
    
    // Dispatch a custom event with the version string
    this.dispatchEvent(new CustomEvent('versionClicked', {
      bubbles: true,
      composed: true,
      detail: {
        version: this._appVersion
      }
    }));
  }

  private _getNormalizedGithubUrl(): string | null {
    const rawValue = this._githubUrl.trim();
    if (!rawValue) {
      return null;
    }

    try {
      const normalized = new URL(rawValue, window.location.href);
      if (!['http:', 'https:'].includes(normalized.protocol)) {
        return null;
      }

      return normalized.toString();
    } catch (_error) {
      return null;
    }
  }

  private _getGithubButtonTitle(): string {
    if (!this._showGithubButton) {
      return '';
    }

    const githubUrl = this._getNormalizedGithubUrl();
    if (githubUrl) {
      return `Open GitHub: ${githubUrl}`;
    }

    return 'GitHub URL not configured';
  }

  private _openGithubUrl(url?: string | null): void {
    const githubUrl = url || this._getNormalizedGithubUrl();
    if (!githubUrl) {
      return;
    }

    window.open(githubUrl, '_blank', 'noopener,noreferrer');
  }

  private _handleGithubButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    this._openGithubUrl();
  }

  // Render the component
  render(): void {
    if (!this.shadowRoot) return;

    const hasMainMenuItems = this._hasMainMenuItems();
    const mobileBreakpoint = this._mobileBreakpoint || 480;
    const githubUrl = this._getNormalizedGithubUrl();
    const githubButtonTitle = this._getGithubButtonTitle();
    const siteNameTitle = this._siteNameLong ? ` title="${escapeAttribute(this._siteNameLong)}"` : '';
    
    const styles = `
      :host {
        --_toolbar-bg: var(--toolbar-background, var(--theme-background-secondary));
        --_toolbar-menu-bg: var(--toolbar-menu-background, var(--_toolbar-bg));
        --_toolbar-text: var(--toolbar-text-color, var(--theme-text-primary));
        --_toolbar-border: var(--toolbar-border-color, var(--theme-border-primary));
        --_toolbar-subtle-text: var(--toolbar-subtle-text-color, var(--theme-text-secondary));
        --_toolbar-link: var(--toolbar-link-color, var(--link-color, var(--theme-accent-primary)));
        --_toolbar-shadow: var(--toolbar-shadow, var(--theme-shadow-sm));
        --_toolbar-button-hover: var(--toolbar-button-hover-background, var(--button-hover-bg));
        --_toolbar-height: var(--toolbar-height, ${this._compact ? '52px' : '96px'});
        --_toolbar-padding-inline: var(--toolbar-padding-inline, var(--theme-space-4, 16px));
        --_toolbar-control-size: var(--toolbar-control-size, var(--button-control-size, 44px));
        --_toolbar-mobile-control-size: var(--toolbar-mobile-control-size, var(--_toolbar-control-size));
        --_toolbar-mobile-hamburger-icon-size: var(--toolbar-mobile-hamburger-icon-size, 20px);
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
        position: relative;
        box-sizing: border-box;
        z-index: 90; /* Ensure toolbar has higher z-index than regular content */
      }
      .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: var(--_toolbar-height);
        padding: 0 var(--_toolbar-padding-inline);
        margin: 0;
        background-color: var(--_toolbar-bg);
        color: var(--_toolbar-text);
        position: relative;
        box-sizing: border-box;
        transition: background-color 0.3s ease, color 0.3s ease, height 0.3s ease;
        border: none;
        border-bottom: 1px solid var(--_toolbar-border);
        z-index: 10; /* Ensure toolbar content has proper stacking */
      }

      .left-section {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex: 1;
        position: relative;
      }
      
      .center-section {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 2;
        position: relative;
        z-index: 50; /* Ensure center content has proper z-index stacking */
        overflow: visible; /* Allow dropdowns to extend beyond toolbar */
      }
      
      .center-section qds-horizontal-menu {
        width: 75%;
        --menu-background: var(--_toolbar-menu-bg);
      }

      /* Keep a consumer-provided center menu visually continuous with the toolbar. */
      .center-section ::slotted(qds-horizontal-menu) {
        --menu-background: var(--_toolbar-menu-bg);
      }

      :host([compact]) .center-section qds-horizontal-menu::part(top-level-item) {
        padding: 8px 16px;
        min-height: 36px;
      }
      
      .right-section {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
      }

      .toolbar-icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: var(--_toolbar-control-size);
        height: var(--_toolbar-control-size);
        min-width: var(--_toolbar-control-size);
        min-height: var(--_toolbar-control-size);
        padding: 0;
        border: none;
        border-radius: var(--theme-radius-sm, 4px);
        background: transparent;
        color: var(--icon-color-primary, var(--_toolbar-text));
        cursor: pointer;
        transition: background-color var(--transition-speed-fast, 0.15s),
                    color var(--transition-speed-fast, 0.15s),
                    box-shadow var(--transition-speed-fast, 0.15s);
      }

      .toolbar-icon-button:hover:not(:disabled) {
        background-color: var(--button-hover-bg, var(--_toolbar-button-hover));
      }

      .toolbar-icon-button:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring);
      }

      .toolbar-icon-button:disabled {
        opacity: 0.45;
        cursor: default;
      }

      .toolbar-icon-button-icon {
        font-size: var(--icon-size, 20px);
        color: currentColor;
        line-height: 1;
      }

      .logo-container {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
      }
      
      /* Copy the exact sitename styles from q-base.css since Shadow DOM blocks external styles */
      .sitename {
        font-weight: 500;
        font-size: 1.125em;
        color: var(--_toolbar-text);
        margin-left: .3em;
        margin-right: .3em;
        font-family: var(--qds-font-family, 'IBM Plex Sans', sans-serif);
        
        
        /* Additional toolbar-specific styles */
        white-space: nowrap;
        overflow: visible;
        text-overflow: clip;
        max-width: 100%;
        min-width: fit-content;
        overflow-wrap: anywhere;
        position: relative; /* For positioning the app-version */
      }
      
      .app-version {
        font-size: ${this._compact ? '0.6em' : '0.55em'};
        font-weight: 300;
        color: var(--_toolbar-subtle-text);
        margin-left: 0.4em;
        vertical-align: sub;
        position: relative;
        bottom: -0.1em;
        cursor: pointer;
        transition: color 0.2s ease;
      }
      
      .app-version:hover {
        color: var(--_toolbar-link);
      }
      
      /* Default content for empty slots */
      .left-section ::slotted(*), 
      .center-section ::slotted(*), 
      .right-section ::slotted(*) {
        margin: 0 4px;
        display: flex;
        align-items: center;
      }
      
      /* Ensure images in slots are vertically centered */
      .left-section ::slotted(img), 
      .center-section ::slotted(img), 
      .right-section ::slotted(img) {
        align-self: center;
      }
      
      /* Hamburger menu button */
      .hamburger-button {
        display: none;
        flex-direction: column;
        justify-content: space-between;
        width: var(--_toolbar-mobile-control-size);
        height: var(--_toolbar-mobile-control-size);
        min-width: var(--_toolbar-mobile-control-size);
        min-height: var(--_toolbar-mobile-control-size);
        flex: 0 0 var(--_toolbar-mobile-control-size);
        background: transparent;
        border: none;
        cursor: pointer;
        padding: calc((var(--_toolbar-mobile-control-size) - var(--_toolbar-mobile-hamburger-icon-size)) / 2);
        box-sizing: border-box;
        z-index: 10;
      }
      
      .hamburger-button span {
        display: block;
        height: 2px;
        width: 100%;
        background-color: var(--_toolbar-text);
        transition: all 0.3s ease;
      }
      
      .hamburger-button.active span:nth-child(1) {
        transform: translateY(9px) rotate(45deg);
      }
      
      .hamburger-button.active span:nth-child(2) {
        opacity: 0;
      }
      
      .hamburger-button.active span:nth-child(3) {
        transform: translateY(-9px) rotate(-45deg);
      }
      
      /* Mobile menu dropdown */
      .mobile-menu {
        display: none;
        position: absolute;
        top: ${this._compact ? '52px' : '96px'};
        right: 0;
        background-color: var(--_toolbar-bg);
        box-shadow: var(--_toolbar-shadow);
        z-index: 1000;
        width: 100%;
        max-width: 300px;
        padding: 12px;
        box-sizing: border-box;
        border-radius: 0 0 4px 4px;
        flex-direction: column;
        transform: translateY(-10px);
        opacity: 0;
        visibility: hidden;
        transition: all 0.2s ease;
        border: 1px solid var(--_toolbar-border);
        border-top: none;
      }
      
      .mobile-menu.open {
        display: flex;
        transform: translateY(0);
        opacity: 1;
        visibility: visible;
      }
  
      .mobile-main-menu {
        display: none;
        width: 100%;
        margin-bottom: 8px;
        padding-bottom: 8px;
      }

      .mobile-main-menu qds-horizontal-menu {
        width: 100%;
      }

      .mobile-main-menu qds-horizontal-menu::part(nav-container) {
        background-color: transparent;
      }

      .mobile-main-menu qds-horizontal-menu::part(top-level-item) {
        padding: 12px 10px;
        border-radius: 6px;
      }

      .mobile-toolbar-menu {
        display: none;
        align-items: center;
        justify-content: center;
      }    
      
      .mobile-right-content {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 0;
      }

      .mobile-scroll-content {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 8px;
      }

      .mobile-action-buttons {
        display: flex;
        width: 100%;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        margin-top: 0;
        padding: 8px;
        flex: 0 0 auto;
        position: sticky;
        bottom: 0;
        z-index: 1;
        background-color: inherit;
        border-top: 1px solid var(--_toolbar-border);
        overflow-x: hidden;
      }

      .mobile-action-buttons qds-help-button,
      .mobile-action-buttons qds-notification-tray,
      .mobile-action-buttons qds-settings-button,
      .mobile-action-buttons qds-auth-avatar,
      .mobile-action-buttons qds-user-avatar {
        display: flex;
        justify-content: flex-start;
      }
      
      .mobile-right-content ::slotted(*) {
        margin: 8px 0 !important;
        width: 100%;
        display: flex !important;
      }

      .mobile-right-content ::slotted(.mobile-nav-tree) {
        display: block !important;
        align-items: stretch !important;
        justify-content: initial !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100%;
      }
      
      /* Fix for mobile menu button styling */
      .mobile-right-content ::slotted(button),
      .mobile-right-content ::slotted(div) {
        align-items: center !important;
        justify-content: flex-start !important;
        padding: 10px !important;
        border-radius: 4px !important;
      }
      
      .mobile-right-content ::slotted(button:hover),
      .mobile-right-content ::slotted(div:hover) {
        background-color: var(--_toolbar-button-hover);
      }
      
      /* Ensure SVG icons display properly */
      .mobile-right-content ::slotted(*) svg,
      .mobile-right-content ::slotted(*) img {
        width: 20px !important;
        height: 20px !important;
        margin-right: 8px !important;
        display: inline-block !important;
      }
      
      /* Responsive adjustments */
      @media (max-width: ${mobileBreakpoint}px) {
        .toolbar {
          padding: 0 12px;
        }

        .left-section {
          flex: 1 1 auto;
          min-width: 0;
          max-width: none;
        }

        .center-section {
          display: none;
        }
        
        /* Hide menu and logo in mobile view, show only site name */
        .logo-container {
          display: flex !important;
          justify-content: flex-start;
        }
        
        .logo-container qds-hierarchical-menu,
        .logo-container qds-company-logo {
          display: none !important;
        }
        
        .logo-container .sitename {
          margin-left: 0;
          max-width: 100%;
          white-space: nowrap;
          font-size: clamp(0.9rem, 3.6vw, 1.125em);
        }
        
        /* Hide build/version label on mobile */
        .app-version {
          display: none;
        }
        
        .right-section {
          display: flex;
          flex: 0 0 auto;
          gap: 8px;
          min-width: 0;
        }

        .right-section qds-help-button,
        .right-section .toolbar-icon-button--github,
        .right-section qds-settings-button,
        .right-section qds-theme-switch {
          display: none;
        }

        .right-section qds-notification-tray,
        .right-section qds-auth-avatar,
        .right-section qds-user-avatar {
          display: inline-flex;
        }
        
        .hamburger-button {
          display: flex;
          order: -1;
          margin-right: 12px;
        }

        .mobile-toolbar-menu {
          display: flex;
          order: -2;
          margin-left: 0;
          margin-right: 12px;
        }

        .mobile-toolbar-menu qds-hierarchical-menu {
          width: var(--_toolbar-mobile-control-size);
          height: var(--_toolbar-mobile-control-size);
        }

        .mobile-toolbar-menu qds-hierarchical-menu::part(menu-container) {
          position: fixed;
          top: ${this._compact ? '52px' : '96px'};
          left: 0;
          right: 0;
          width: 100%;
          max-width: 100%;
          min-height: 0;
          max-height: calc(100vh - ${this._compact ? '52px' : '96px'});
          margin-top: 0;
          border-radius: 0;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        .mobile-menu {
          display: flex;
          width: 75%;
          max-width: 75%;
          left: 0;
          right: auto;
          padding: 12px 12px 0 12px;
          height: calc(100vh - ${this._compact ? '52px' : '96px'});
          height: calc(100dvh - ${this._compact ? '52px' : '96px'});
          max-height: calc(100dvh - ${this._compact ? '52px' : '96px'});
          overflow-y: hidden;
          overflow-x: hidden;
        }

        .mobile-right-content {
          flex: 1;
          height: 100%;
          min-height: 0;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }

        .mobile-main-menu {
          display: ${hasMainMenuItems ? 'block' : 'none'};
        }

        .mobile-action-buttons {
          display: none;
        }

        .gradient-line-wrapper {
          display: ${this._showMobileGradientLine ? 'block' : 'none'};
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="toolbar" part="toolbar">
        <div class="left-section" part="left-section">
          <slot name="preleft">
          </slot>
          <slot name="left">
            <div class="logo-container">
              <qds-hierarchical-menu app-name="Quantum Design System"> </qds-hierarchical-menu>
              <qds-company-logo q-only height="${this._compact ? Math.floor(52 * 0.5) : Math.floor(96 * 0.5)}"${siteNameTitle}></qds-company-logo>
              ${this._siteName ? `
                <span class="sitename" part="site-name"${siteNameTitle}>
                  ${this._siteName}
                  ${this._appVersion ? `<span class="app-version" part="app-version" id="app-version">${this._appVersion}</span>` : ''}
                </span>` : ''}
            </div>              
          </slot>
        </div>
        <div class="center-section" part="center-section">
          <slot name="center">
            <qds-horizontal-menu data="[]"></qds-horizontal-menu>
          </slot>
        </div>
        <div class="right-section" part="right-section">
          <slot name="right">
            ${this._showHelpButton ? '<qds-help-button></qds-help-button>' : ''}
            ${this._showGithubButton ? `
              <button
                type="button"
                class="toolbar-icon-button toolbar-icon-button--github"
                part="github-button"
                data-role="github-button"
                aria-label="Open GitHub"
                title="${githubButtonTitle}"
                ${githubUrl ? '' : 'disabled'}
              >
                <i class="pi pi-github toolbar-icon-button-icon" aria-hidden="true"></i>
              </button>` : ''}
            ${this._showNotificationTray ? '<qds-notification-tray></qds-notification-tray>' : ''}
            ${this._showSettingsButton ? '<qds-settings-button></qds-settings-button>' : ''}
            ${this._showThemeSwitch ? '<qds-theme-switch compact></qds-theme-switch>' : ''}
            ${this._usesQdsAuthentication() && this._hasAuthConfig()
              ? `<qds-auth-avatar id="main-toolbar-auth-avatar" auto-login></qds-auth-avatar>`
              : `<qds-user-avatar></qds-user-avatar>`
            }
          </slot>
        </div>
        
        <!-- Mobile hamburger button -->
        <button class="hamburger-button" part="hamburger-button">
          <span></span>
          <span></span>
          <span></span>
        </button>

        <!-- Mobile hierarchical menu trigger on the right side -->
        <div class="mobile-toolbar-menu" part="mobile-toolbar-menu">
          <qds-hierarchical-menu app-name="Quantum Design System"></qds-hierarchical-menu>
        </div>
        
        <!-- Mobile dropdown menu -->
        <div class="mobile-menu" part="mobile-menu">
          <div class="mobile-right-content">
            <div class="mobile-scroll-content" part="mobile-scroll-content">
              <div class="mobile-main-menu" part="mobile-main-menu">
                <qds-horizontal-menu data="[]"></qds-horizontal-menu>
              </div>
              <slot name="mobile-right"></slot>
            </div>
            <div class="mobile-action-buttons" part="mobile-action-buttons">
              ${this._usesQdsAuthentication() && this._hasAuthConfig()
            ? `<qds-auth-avatar id="main-toolbar-auth-avatar" auto-login></qds-auth-avatar>`
            : `<qds-user-avatar></qds-user-avatar>`}
              ${this._showNotificationTray ? '<qds-notification-tray></qds-notification-tray>' : ''}
              ${this._showSettingsButton ? '<qds-settings-button></qds-settings-button>' : ''}
              ${this._showThemeSwitch ? '<qds-theme-switch compact></qds-theme-switch>' : ''}
              <qds-help-button></qds-help-button>
            </div>
          </div>
        </div>
        
        <!-- Gradient line at bottom of toolbar -->
      </div>
      <div class="gradient-line-wrapper">
        <qds-gradient-line class="toolbar-gradient-line"></qds-gradient-line>
      </div>
    `;
    void adoptPrimeIconsStyles(this.shadowRoot);

    // Add CSS for the gradient line
    const style = document.createElement('style');
    style.textContent = `
      .gradient-line-wrapper {
        position: relative;
        z-index: 5; /* Lower z-index to ensure dropdowns appear on top */
        width: 100%;
      }
      
      .toolbar-gradient-line {
        display: block;
        width: 100%;
        margin-top: -1px; /* Ensure no gap between toolbar and line */
      }
    `;
    this.shadowRoot.appendChild(style);
    
    // Ensure right-side actions are placed in the correct container for current viewport
    this._positionMobileActions();

    // Reattach event listeners after rendering
    const hamburgerButton = this.shadowRoot.querySelector('.hamburger-button') as HTMLButtonElement | null;
    if (hamburgerButton) {
      hamburgerButton.addEventListener('click', this._boundMobileMenuToggle);
    }
    const mobileMainMenu = this.getMobileMainMenu();
    if (mobileMainMenu) {
      mobileMainMenu.addEventListener('itemSelected', this._boundMobileMainMenuSelect);
    }
    const mobileToolbarMenu = this.shadowRoot.querySelector('.mobile-toolbar-menu qds-hierarchical-menu') as HTMLElement | null;
    if (mobileToolbarMenu) {
      mobileToolbarMenu.addEventListener('toggle', this._boundMobileToolbarMenuToggle);
    }
    // Attach click handler to app version
    const appVersion = this.shadowRoot.querySelector('#app-version') as HTMLElement | null;
    if (appVersion) {
      appVersion.addEventListener('click', this._boundVersionClick);
    }
    const githubButton = this.getGithubButton();
    if (githubButton) {
      githubButton.addEventListener('click', this._boundGithubButtonClick);
    }
    
    // Apply main menu data if available
    if (this._mainMenuData) {
      // Use setTimeout to ensure the menu is fully rendered
      setTimeout(() => this._updateMainMenu(), 0);
    }

    setTimeout(() => {
      this._positionMobileActions();
      this._syncMobileAvatarMenu();
    }, 0);
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-main-toolbar')) {
  customElements.define('qds-main-toolbar', QdsMainToolbar);
}

export { QdsMainToolbar };
